import { SpreadsheetActions } from "@/components/tickets/SpreadsheetPreviewDialog";
import { spreadsheetKind } from "@/lib/spreadsheetPreview";
import { AudioAttachmentPlayer } from "@/components/tickets/AudioAttachmentPlayer";
import { ImpersonationBanner } from "@/components/client/ImpersonationBanner";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { PendingFileRow } from "@/components/PendingFileRow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { linkifyText } from "@/lib/linkify";
import {
  getClientPortalBase,
  setClientTenant,
} from "@/lib/clientPortalPaths";
import type { Call, CallStatus } from "@/services/call.service/call.api";
import { clientCallService } from "@/services/call.service/clientCall.api";
import { clientAuthService } from "@/services/clientAuth.service/clientAuth.api";
import {
  clientTicketService,
  ClientTicket,
  ClientTicketMessage,
  ClientUserOption,
  TicketAttachmentRef,
} from "@/services/clientTicket.service/clientTicket.api";
import { ArrowLeft, Loader2, MessageSquare, Mic, Paperclip, RotateCcw, Send, Sparkles, Square, UserPlus, Video } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

const CLIENT_MESSAGES_LIMIT = 200;

const statusLabel: Record<string, string> = {
  open: "Aberto",
  in_progress: "Em andamento",
  resolved: "Resolvido",
};

/** Chamada em `ringing`/`active` justifica o banner — encerrada/recusada não. */
const ONGOING_CALL_STATUSES: CallStatus[] = ["ringing", "active"];

function isAudioAttachment(a: TicketAttachmentRef): boolean {
  if (a.mimeType?.startsWith("audio/")) return true;
  return /\.(webm|mp3|wav|m4a|ogg|opus|aac)$/i.test(a.name || "");
}

/** Compara ids vindos da API (string vs ObjectId serializado) para lookup em listas. */
function sameId(a: string | undefined, b: string | undefined): boolean {
  if (a == null || b == null) return false;
  return String(a).trim() === String(b).trim();
}

function AttachmentList({
  items,
  inverse,
  audioFooter,
}: {
  items: TicketAttachmentRef[];
  inverse?: boolean;
  audioFooter?: (a: TicketAttachmentRef, index: number) => ReactNode;
}) {
  if (!items?.length) return null;
  const linkClass = "text-primary underline underline-offset-2";
  return (
    <div className="flex flex-col gap-2 mt-2">
      {items.map((a, i) => (
        <div
          key={`${a.url}-${i}`}
          className={`rounded border p-2 text-xs break-all ${
            inverse ? "border-border/60 bg-background/40" : "border-border/50"
          }`}
        >
          {a.mimeType?.startsWith("image/") ? (
            <a href={a.url} target="_blank" rel="noreferrer" className="block">
              <img src={a.url} alt={a.name} className="max-h-48 rounded object-contain" />
              <span className={linkClass}>{a.name}</span>
            </a>
          ) : isAudioAttachment(a) ? (
            <div className="space-y-2">
              <AudioAttachmentPlayer src={a.url} label={a.name} compact />
              {audioFooter?.(a, i)}
            </div>
          ) : a.mimeType?.startsWith("video/") ? (
            <div>
              <p className="mb-1">{a.name}</p>
              <video controls src={a.url} className="max-h-64 w-full rounded" />
            </div>
          ) : spreadsheetKind(a) ? (
            <div>
              <p className="mb-1">{a.name}</p>
              <SpreadsheetActions attachment={a} className="flex gap-2" />
            </div>
          ) : (
            <a href={a.url} target="_blank" rel="noreferrer" className={linkClass}>
              {a.name}
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ClientTicketDetail() {
  const { protocolId, tenant: tenantParam } = useParams<{ protocolId: string; tenant?: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<ClientTicket | null>(null);
  const [messages, setMessages] = useState<ClientTicketMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [clientUsers, setClientUsers] = useState<ClientUserOption[]>([]);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [addingMembers, setAddingMembers] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordStreamRef = useRef<MediaStream | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const prevMsgCountRef = useRef(0);
  const [activeCall, setActiveCall] = useState<Call | null>(null);

  const loadTicket = useCallback(async () => {
    if (!protocolId) return;
    const [t] = await clientTicketService.getTicket(protocolId);
    if (t) setTicket(t);
  }, [protocolId]);

  const loadActiveCall = useCallback(async () => {
    if (!protocolId) return;
    const [res] = await clientCallService.getActiveForTicket(protocolId);
    setActiveCall(res ?? null);
  }, [protocolId]);

  const loadMessages = useCallback(async () => {
    if (!protocolId) return;
    const [res] = await clientTicketService.getMessages(protocolId, 1, CLIENT_MESSAGES_LIMIT);
    if (res?.result) {
      const sorted = [...res.result].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setMessages(sorted);
    }
  }, [protocolId]);

  useEffect(() => {
    const t = tenantParam?.trim();
    if (t) setClientTenant(t);
  }, [tenantParam]);

  useEffect(() => {
    prevMsgCountRef.current = 0;
    nearBottomRef.current = true;
  }, [protocolId]);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    nearBottomRef.current = scrollHeight - clientHeight - scrollTop < 120;
  };

  useLayoutEffect(() => {
    if (loading) return;
    if (messages.length === 0) return;
    const grew = messages.length > prevMsgCountRef.current;
    const firstPaint = prevMsgCountRef.current === 0;
    prevMsgCountRef.current = messages.length;
    if (firstPaint || (grew && nearBottomRef.current)) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: firstPaint ? "auto" : "smooth" });
      });
    }
  }, [messages, loading]);

  useEffect(() => {
    if (!protocolId || protocolId === "new") return;
    const id = window.setInterval(() => {
      void loadMessages();
      void loadTicket();
      void loadActiveCall();
    }, 5000);
    return () => clearInterval(id);
  }, [protocolId, loadMessages, loadTicket, loadActiveCall]);

  useEffect(() => {
    if (!clientAuthService.isAuthenticated()) {
      navigate(`${getClientPortalBase()}/login`);
      return;
    }
    if (!protocolId || protocolId === "new") {
      navigate(`${getClientPortalBase()}/tickets`);
      return;
    }
    (async () => {
      setLoading(true);
      await loadTicket();
      await loadMessages();
      setLoading(false);
      void loadActiveCall();
    })();
  }, [protocolId, navigate, loadTicket, loadMessages, loadActiveCall]);

  useEffect(() => {
    if (ticket) {
      clientTicketService.getClientUsers().then(([users]) => {
        if (users && Array.isArray(users)) setClientUsers(users);
      });
    }
  }, [ticket?._id]);

  useEffect(() => {
    return () => {
      recordStreamRef.current?.getTracks().forEach((tr) => tr.stop());
    };
  }, []);

  const isResolved = ticket?.status === "resolved";
  const canInteract = ticket && !isResolved;

  const startReplyRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      recordChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) recordChunksRef.current.push(ev.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        recordStreamRef.current = null;
        const blob = new Blob(recordChunksRef.current, { type: mr.mimeType || "audio/webm" });
        const file = new File([blob], `gravacao-${Date.now()}.webm`, {
          type: blob.type || "audio/webm",
        });
        setPendingFiles((prev) => [...prev, file]);
        toast.success("Áudio gravado. Envie para anexar à mensagem.");
      };
      mr.start(200);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch {
      toast.error("Não foi possível acessar o microfone.");
    }
  };

  const stopReplyRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
  };

  const handlePolishMessage = async () => {
    const t = newMessage.trim();
    if (!t) {
      toast.error("Digite um texto para melhorar.");
      return;
    }
    setAiBusy(true);
    const [data, err] = await clientTicketService.polishMessage(t);
    setAiBusy(false);
    if (err?.msg) {
      toast.error(err.msg);
      return;
    }
    if (data?.text) setNewMessage(data.text);
  };

  const handleSendMessage = async () => {
    if (!protocolId || (!newMessage.trim() && pendingFiles.length === 0)) return;
    setSending(true);
    const attachments: TicketAttachmentRef[] = [];
    for (const file of pendingFiles) {
      const [ref, err] = await clientTicketService.uploadAttachment(file);
      if (err?.msg || !ref) {
        toast.error(err?.msg || "Falha no upload");
        setSending(false);
        return;
      }
      attachments.push(ref);
    }
    const [res] = await clientTicketService.sendMessage(protocolId, {
      content: newMessage.trim() || undefined,
      attachments: attachments.length ? attachments : undefined,
    });
    setSending(false);
    if (res) {
      setNewMessage("");
      setPendingFiles([]);
      loadMessages();
    }
  };

  const handleCloseTicket = async () => {
    if (!protocolId || !ticket) return;
    setClosing(true);
    const [updated, err] = await clientTicketService.updateTicket(protocolId, {
      status: "resolved",
    });
    setClosing(false);
    if (err?.msg) {
      toast.error(err.msg);
      return;
    }
    if (updated) setTicket(updated);
    toast.success("Ticket encerrado.");
  };

  const handleReopenTicket = async () => {
    if (!protocolId || !ticket) return;
    setReopening(true);
    const [updated, err] = await clientTicketService.updateTicket(protocolId, {
      status: "open",
    });
    setReopening(false);
    if (err?.msg) {
      toast.error(err.msg);
      return;
    }
    if (updated) setTicket(updated);
    loadMessages();
    toast.success("Ticket reaberto.");
  };

  const handleAddMembers = async () => {
    if (!protocolId || selectedUserIds.length === 0) return;
    setAddingMembers(true);
    const [res] = await clientTicketService.addMembers(protocolId, selectedUserIds);
    setAddingMembers(false);
    if (res) {
      setAddMembersOpen(false);
      setSelectedUserIds([]);
      loadTicket();
      toast.success("Pessoas adicionadas ao ticket.");
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!protocolId) return;
    const [res] = await clientTicketService.removeMember(protocolId, userId);
    if (res) loadTicket();
  };

  const user = clientAuthService.getCurrentUser();
  const isImpersonating = clientAuthService.isImpersonating();
  if (!user) return null;

  if (loading && !ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Ticket não encontrado.</p>
        <Button variant="outline" onClick={() => navigate(`${getClientPortalBase()}/tickets`)}>
          Voltar
        </Button>
      </div>
    );
  }

  const memberIds = ticket.memberIds || [];
  const currentUserId = user._id;
  const memberUsersById = new Map(
    (ticket.memberUsers || []).map((m) => [String(m._id), m])
  );
  const availableClientUsers = clientUsers
    .filter(
      (u) =>
        !sameId(u._id, currentUserId) && !memberIds.some((mid) => sameId(mid, u._id))
    )
    .filter((u) => {
      const term = memberSearch.trim().toLowerCase();
      if (!term) return true;
      return (
        String(u.name || "").toLowerCase().includes(term) ||
        String(u.email || "").toLowerCase().includes(term)
      );
    });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background via-background to-muted/20">
      {isImpersonating && <ImpersonationBanner />}
      <header className="sticky top-0 z-10 border-b border-border/80 bg-card/90 backdrop-blur-md supports-[backdrop-filter]:bg-card/75 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Button variant="ghost" size="sm" onClick={() => navigate(`${getClientPortalBase()}/tickets`)} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Meus tickets
          </Button>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="hidden sm:inline text-sm text-muted-foreground">{user.name}</span>
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => clientAuthService.logout()}>
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 flex-1 flex flex-col gap-6">
        {activeCall && ONGOING_CALL_STATUSES.includes(activeCall.status) && (
          <div className="rounded-xl border border-green-500/20 bg-green-500/10 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Video className="h-5 w-5 text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-medium">
                  {activeCall.status === "ringing" ? "Chamada de vídeo recebida" : "Chamada de vídeo em andamento"}
                </p>
                <p className="text-sm text-muted-foreground">
                  A equipe de suporte está te chamando por videochamada.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate(`${getClientPortalBase()}/chamada/${activeCall._id}`)}
            >
              {activeCall.status === "ringing" ? "Atender" : "Entrar na chamada"}
            </Button>
          </div>
        )}
        <Card className="border-border/60 shadow-sm overflow-hidden">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div className="space-y-1">
                <CardTitle className="text-xl sm:text-2xl leading-tight">{ticket.title || ticket.protocolId}</CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs">{ticket.protocolId}</span>
                  <Badge variant="secondary" className="font-normal">
                    {statusLabel[ticket.status] || ticket.status}
                  </Badge>
                </CardDescription>
              </div>
              {canInteract ? (
                <Button variant="destructive" size="sm" onClick={handleCloseTicket} disabled={closing}>
                  {closing ? "Encerrando…" : "Fechar ticket"}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleReopenTicket} disabled={reopening}>
                  <RotateCcw className="h-4 w-4 mr-1.5" />
                  {reopening ? "Reabrindo…" : "Reabrir ticket"}
                </Button>
              )}
            </div>
            {ticket.description && (
              <p className="text-sm text-muted-foreground mt-2">{ticket.description}</p>
            )}
            {ticket.attachments && ticket.attachments.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Anexos do chamado</p>
                <AttachmentList items={ticket.attachments} />
              </div>
            )}
          </CardHeader>
        </Card>

        <div className="rounded-xl border border-border/60 bg-card/50 p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
              <UserPlus className="h-4 w-4 shrink-0 text-muted-foreground" />
              Pessoas no ticket
            </h2>
            {canInteract && (
              <Dialog open={addMembersOpen} onOpenChange={setAddMembersOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">Adicionar pessoa</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Adicionar pessoas ao ticket</DialogTitle>
                    <DialogDescription>
                      Selecione usuários da sua organização para incluir no ticket.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    <Input
                      placeholder="Buscar pessoa por nome ou e-mail..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                    />
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                    {availableClientUsers.map((u) => (
                        <label key={u._id} className="flex items-center gap-2 cursor-pointer rounded-md px-2 py-1.5 hover:bg-muted/40">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u._id)}
                            onChange={(e) =>
                              setSelectedUserIds((prev) =>
                                e.target.checked ? [...prev, u._id] : prev.filter((x) => x !== u._id)
                              )
                            }
                          />
                          <span>{u.name}</span>
                          <span className="text-muted-foreground text-sm">({u.email})</span>
                        </label>
                      ))}
                    {availableClientUsers.length === 0 && (
                      <p className="text-sm text-muted-foreground">Nenhum outro usuário disponível.</p>
                    )}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddMembersOpen(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleAddMembers} disabled={addingMembers || selectedUserIds.length === 0}>
                      {addingMembers ? "Adicionando..." : "Adicionar"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {memberIds.map((mid) => {
              const memberUser =
                clientUsers.find((u) => sameId(u._id, mid)) || memberUsersById.get(String(mid));
              const midKey = String(mid);
              return (
                <Badge key={midKey} variant="secondary" className="gap-1 max-w-full h-auto items-center py-1.5 px-2.5">
                  <span className="min-w-0 flex-1 text-left font-medium leading-tight truncate">
                    {memberUser ? memberUser.name : "Usuário"}
                  </span>
                  {canInteract && !sameId(mid, currentUserId) && (
                    <button
                      type="button"
                      className="ml-1 shrink-0 hover:text-destructive self-start"
                      onClick={() => handleRemoveMember(midKey)}
                      aria-label="Remover"
                    >
                      ×
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>
        </div>

        <Card className="border-border/60 shadow-sm flex flex-col flex-1 min-h-0">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
              Mensagens
            </CardTitle>
            {isResolved && (
              <CardDescription>
                Este ticket está encerrado. Você pode visualizar o histórico ou reabri-lo para voltar a conversar.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4 flex flex-col flex-1 min-h-0 pt-0">
            <div
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
              className="space-y-3 min-h-[min(50vh,320px)] max-h-[min(70vh,720px)] flex-1 overflow-y-auto scroll-smooth pr-1 -mr-1"
            >
              {messages.filter((m) => m.type !== "private_note").map((m) => (
                <div
                  key={m._id}
                  className={`rounded-2xl px-3.5 py-2.5 sm:px-4 sm:py-3 max-w-[92%] sm:max-w-[85%] border shadow-sm ${
                    sameId(m.contact?._id, currentUserId)
                      ? "ml-auto mr-0 border-primary/25 bg-primary/[0.06] text-foreground dark:border-primary/30 dark:bg-primary/[0.08]"
                      : "ml-0 mr-auto border-border/70 bg-muted/80 dark:bg-muted/50"
                  }`}
                >
                  <p className="text-[11px] sm:text-xs font-semibold mb-1.5 text-muted-foreground tracking-tight">
                    {sameId(m.contact?._id, currentUserId)
                      ? "Você"
                      : m.type === "agent" || m.type === "AGENT"
                        ? m.agent?.name?.trim() || "Equipe"
                        : m.contact?.name?.trim() || "Cliente"}
                  </p>
                  {m.content?.msg && (
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{linkifyText(m.content.msg)}</p>
                  )}
                  {m.content?.attachments && m.content.attachments.length > 0 && (
                    <div className={m.content?.msg ? "mt-2" : ""}>
                      <AttachmentList
                        items={m.content.attachments}
                        inverse={sameId(m.contact?._id, currentUserId)}
                        audioFooter={(a) => {
                          if (!isAudioAttachment(a)) return null;
                          const transcriptText = a.transcript;
                          return (
                            <div className="rounded-md border border-border/40 bg-muted/20 px-2 py-1.5 space-y-1.5">
                              {transcriptText && (
                                <p className="text-xs whitespace-pre-wrap text-muted-foreground leading-relaxed border-t border-border/30 pt-2">
                                  {transcriptText}
                                </p>
                              )}
                            </div>
                          );
                        }}
                      />
                    </div>
                  )}
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-2 tabular-nums">
                    {new Date(m.createdAt).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              ))}
              <div ref={messagesEndRef} aria-hidden className="h-px w-full shrink-0" />
            </div>
            {canInteract && isImpersonating && (
              <p className="text-xs text-muted-foreground pt-2 border-t border-border/60 mt-2">
                Envio de mensagens desativado — você está vendo este ticket em modo admin (acessando como o cliente).
              </p>
            )}
            {canInteract && !isImpersonating && (
              <div className="space-y-2 pt-2">
                {pendingFiles.length > 0 && (
                  <ul className="text-sm border rounded-md p-2 max-h-48 overflow-y-auto">
                    {pendingFiles.map((f, i) => (
                      <PendingFileRow
                        key={`${f.name}-${i}`}
                        file={f}
                        onRemove={() =>
                          setPendingFiles((prev) => prev.filter((_, j) => j !== i))
                        }
                      />
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  <label
                    className={`cursor-pointer inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground ${
                      isRecording ? "pointer-events-none opacity-50" : ""
                    }`}
                  >
                    <Paperclip className="h-4 w-4" />
                    Anexar
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      disabled={isRecording}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setPendingFiles((prev) => [...prev, ...files]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {!isRecording ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1"
                      disabled={sending || aiBusy}
                      onClick={startReplyRecording}
                    >
                      <Mic className="h-3 w-3" />
                      Gravar
                    </Button>
                  ) : (
                    <Button type="button" variant="destructive" size="sm" className="h-8 gap-1" onClick={stopReplyRecording}>
                      <Square className="h-3 w-3" />
                      Parar
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-8 gap-1"
                    disabled={aiBusy || isRecording || !newMessage.trim()}
                    onClick={handlePolishMessage}
                  >
                    {aiBusy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    IA
                  </Button>
                </div>
                <div className="flex gap-2 items-end">
                  <Input
                    className="min-h-[44px] rounded-xl border-border/80 bg-background/80"
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                  />
                  <Button
                    className="h-11 w-11 shrink-0 rounded-xl"
                    onClick={handleSendMessage}
                    disabled={
                      sending ||
                      aiBusy ||
                      isRecording ||
                      (!newMessage.trim() && pendingFiles.length === 0)
                    }
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
